/* Unified notifications for both the browser tab and the installed PWA.
   Uses the service worker registration's showNotification when available
   (works even for the installed desktop app) and falls back to the
   Notification constructor. Always attaches the app logo as icon/badge. */

let swReg = null;

const ICON = "/favicon-192.png"; // full-colour app logo
const BADGE = "/favicon-64.png"; // small monochrome-ish badge (Android status bar)

export async function initNotifications() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return null;
  try {
    await navigator.serviceWorker.register("/sw.js");
    swReg = await navigator.serviceWorker.ready;
  } catch {
    swReg = null;
  }
  return swReg;
}

export function canNotify() {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    Notification.permission === "granted"
  );
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// Subscribe this device to web push so notifications arrive even when the
// installed PWA window is in the background. Safe to call repeatedly.
export async function subscribeToPush(api) {
  try {
    if (!canNotify()) return;
    const reg = swReg || (await navigator.serviceWorker.ready);
    if (!reg || !reg.pushManager) return;
    const { data } = await api.get("/push/vapid-public-key");
    if (!data || !data.key) return;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.key),
      });
    }
    await api.post("/push/subscribe", { subscription: sub.toJSON() });
  } catch {
    /* ignore: push stays best-effort */
  }
}

export async function showAppNotification(title, body, opts = {}) {
  if (!canNotify()) return;
  const options = {
    body,
    icon: ICON,
    badge: BADGE,
    tag: opts.tag,
    renotify: false,
    data: { url: opts.url || "/" },
  };
  try {
    const reg = swReg || (navigator.serviceWorker && (await navigator.serviceWorker.getRegistration()));
    if (reg && reg.showNotification) {
      await reg.showNotification(title, options);
      return;
    }
  } catch {
    /* fall through to Notification constructor */
  }
  try {
    new Notification(title, options);
  } catch {
    /* ignore */
  }
}
